import { type Ref } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useMessageWebhooks, matchesConversation } from "@/composables/messages/useMessageWebhooks";
import type { Conversation, Message } from "@/dtos/Unipile.dto";
import type { UseWhatsAppMessageWebhooksOptions } from "./types";

export function useWhatsAppMessageWebhooks(options: UseWhatsAppMessageWebhooksOptions) {
  const { selectedConversation, messages, onMessageReceived } = options;
  const queryClient = useQueryClient();

  const sortByTimestamp = (a: Message, b: Message) => {
    const timeA = new Date((a as any).timestamp || (a as any).createdAt || 0).getTime();
    const timeB = new Date((b as any).timestamp || (b as any).createdAt || 0).getTime();
    return timeA - timeB;
  };

  const { setupSocket, cleanupSocket } = useMessageWebhooks({
    onWhatsAppMessage: (payload, helpers) => {
      try {
        const conversation = selectedConversation.value;

        if (!conversation || !matchesConversation(payload, conversation)) {
          queryClient.invalidateQueries({ queryKey: ["whatsapp-conversations"] });
          return;
        }

        const newMessage = helpers.createMessage(payload);
        const messageId = newMessage.id || (newMessage as any).unipileMessageId;
        if (!messageId) return;

        const transformedMessage: Message = {
          ...newMessage,
          conversationId: conversation.id,
          channel: "whatsapp",
        };

        if (messages) {
          const current = messages.value || [];

          if (helpers.checkMessageExists(newMessage, current)) return;

          const optimisticIndex = current.findIndex((msg) => {
            const msgData = msg as any;
            return (
              msgData.unipileMessageId === messageId ||
              (msg.id?.startsWith("temp-") && msgData.isPending)
            );
          });

          if (optimisticIndex !== -1) {
            const updatedMessages = [...current];
            updatedMessages[optimisticIndex] = {
              ...transformedMessage,
              _stableKey: current[optimisticIndex].id,
            } as Message;
            messages.value = updatedMessages;
          } else {
            messages.value = [...current, transformedMessage].sort(sortByTimestamp);
          }
        }

        queryClient.invalidateQueries({ queryKey: ["whatsapp-conversations"] });

        if (onMessageReceived) {
          onMessageReceived(transformedMessage);
        }
      } catch (error) {
        console.error("Error handling WhatsApp message received socket:", error);
      }
    },
    onMessageReaction: (payload) => {
      try {
        if (!messages) return;
        const current = messages.value || [];
        const idx = current.findIndex(
          (msg) =>
            (msg as any).unipileMessageId === payload.unipileMessageId ||
            msg.id === payload.messageId
        );
        if (idx === -1) return;

        const updatedMessages = [...current];
        updatedMessages[idx] = {
          ...updatedMessages[idx],
          reactions: payload.reactions,
        } as Message;
        messages.value = updatedMessages;
      } catch (error) {
        console.error("Error handling WhatsApp message reaction socket:", error);
      }
    },
  });

  return {
    cleanup: cleanupSocket,
  };
}
